import {
  SubscriptionStatus,
  updateWaitinghQueueBypass,
  updateWaitingQueue,
  useAppDispatch,
  useAppSelector,
  useUserInfo,
} from "@next-platform/client-common-core";
import { JWT_PUBLIC_KEY, QUEUE_RETRY_TIME_SECONDS } from "@next-platform/client-common-core/infra/constants";
import basicAppConfig from "@next-platform/client-common-core/infra/constants/basicAppConfig";
import { useEffect } from "react";
import { useParams, useSearchParams } from "react-router-dom";

import useApi from "@/components/useApi";
import { QueueStatus } from "@/constants";
import { verifyToken } from "@/utils";

const publicKey = JSON.parse(JWT_PUBLIC_KEY);
const QUEUE_RETRY_AFTER = parseInt(QUEUE_RETRY_TIME_SECONDS);

const WaitingQueue = () => {
  const { jsonApiService, callApi } = useApi();
  const dispatch = useAppDispatch();
  const applicationId = basicAppConfig.applicationId;
  const { status, bypass } = useAppSelector((state) => state?.waitingQueue) || {};
  const { characterId } = useParams();
  const [searchParams] = useSearchParams();
  const bypassParam = searchParams.get("bypass");
  const { status: userSubscriptionStatus } = useUserInfo();

  const isUserSubscriptionActive = userSubscriptionStatus === SubscriptionStatus.ACTIVE;

  useEffect(() => {
    const load = async () => {
      callApi(jsonApiService.fetchResource, "queue")
        .then((res) => {
          dispatch(updateWaitingQueue(res));
          if (res?.retry_after) {
            setTimeout(load, res?.retry_after);
          }
        })
        .catch(() => {
          if (isUserSubscriptionActive) {
            dispatch(updateWaitingQueue({ status: QueueStatus.ACCESS }));
            return;
          }
          dispatch(updateWaitingQueue({ status: QueueStatus.WAITING }));
          setTimeout(load, QUEUE_RETRY_AFTER);
        });
    };

    // Means we have loded the right information about the user and the application configuration
    if (applicationId) {
      load();
    }
  }, [callApi, jsonApiService.fetchResource, dispatch, applicationId, isUserSubscriptionActive]);

  // if the user has a bypass token and it is valid, we allow them to bypass the queue
  useEffect(() => {
    const verify = async () => {
      const decoded = await verifyToken(bypassParam, publicKey);
      if (decoded?.id === characterId) {
        dispatch(updateWaitinghQueueBypass(decoded?.id));
      }
    };
    if (bypassParam) {
      verify();
    }
    //We hardocde bypassing character 65278037-0b5b-4547-bea3-582618f3e6fa
    if (characterId === "65278037-0b5b-4547-bea3-582618f3e6fa") dispatch(updateWaitinghQueueBypass(characterId));

    if (bypass && characterId && bypass !== characterId) dispatch(updateWaitinghQueueBypass(null));
  }, [characterId, dispatch, bypassParam, bypass]);

  //we expire the status after 20 minutes in case the user is no longer active
  useEffect(() => {
    let timeoutId;
    if (status === "access") timeoutId = setTimeout(() => dispatch(updateWaitingQueue({ status: "expired" })), 20 * 60 * 1000);
    return () => {
      clearTimeout(timeoutId);
    };
  }, [status, dispatch]);

  // refresh queue status if the status changed to refresh
  useEffect(() => {
    const load = async () => {
      callApi(jsonApiService.fetchResource, "queue")
        .then((res) => {
          dispatch(updateWaitingQueue(res));
          if (res?.retry_after) setTimeout(load, res.retry_after);
        })
        .catch(() => {
          if (isUserSubscriptionActive) {
            dispatch(updateWaitingQueue({ status: QueueStatus.ACCESS }));
            return;
          }
          dispatch(updateWaitingQueue({ status: "waiting" }));
        });
    };
    if (status === "refresh") load();
  }, [callApi, jsonApiService.fetchResource, dispatch, status]);

  return null;
};

export default WaitingQueue;
